<template>
	<VDialog
		class="files-advanced-upload-dialog"
		width="552px"
		closable
	>
		<template #header>
			Добавить файл(ы)
		</template>

		<VSelect
			label="Куда прикрепить файлы"
			layout="vertical"
			class="options-select"
			:options="entities"
			v-model="selectedEntity"
		>
			<template #helper-text>
				Выберите опцию, к которой хотите прикрепить файлы
			</template>
		</VSelect>

		<VDataGridV2
      v-if="pendingFiles.length > 0"
			class="files-list"
			:data="pendingFiles"
		>
			<template #empty>
				<div class="empty-files-section">
					<VIcon
						theme="outlined"
						material="upload"
					/>

					<div class="title">
						Для загрузки перетащите файл в это поле или нажмите кнопку
					</div>

					<VFilesUploadButton
						v-if="selectedEntity"
						:entity="selectedEntity"
						disable-upload
						theme="primary"
						@selected="handleLocalSelect"
					>
						Загрузить файл
					</VFilesUploadButton>

					<div class="description">
						Допускаются файлы с расширением: PDF, JPEG и DOC
					</div>
				</div>
			</template>

			<VDataGridColumnV2
				id="name"
				label="Наименование файла"
				width="150px"
        content-class="ellipsis"
			>
				<template #default="{ row }">
					<div class="ellipsis" :title="row.name">
						{{ row.name }}
					</div>
				</template>
			</VDataGridColumnV2>

			<VDataGridColumnV2
				id="size"
        label="Размер файла"
				width="120px"
				content-align="right"
        content-class="ellipsis"
			>
				<template #default="{ row }">
					<div class="ellipsis nowrap" :title="Utils.file.formatSize(row.size)">
						{{ Utils.file.formatSize(row.size) }}
					</div>
				</template>
			</VDataGridColumnV2>

			<VDataGridColumnV2
				id="actions"
				content-align="center"
				width="50px"
			>
				<template #default="{ row }">
					<VButton @click="deletePendingFile(row)">
						<template #icon>
							<VIcon material="delete"/>
						</template>
					</VButton>
				</template>
			</VDataGridColumnV2>
		</VDataGridV2>

		<div
			class="load-clue-section"
		>
			<VIcon
				theme="outlined"
				material="upload"
			/>

			<div class="title">
				Для загрузки перетащите файл в это поле или нажмите кнопку
			</div>

			<VFilesUploadButton
				v-if="selectedEntity"
				:entity="selectedEntity"
				theme="primary"
				disable-upload
        hide-icon
				@selected="handleLocalSelect"
			>
				Выбрать файл
			</VFilesUploadButton>

			<div class="description"></div>
		</div>

		<template #footer>
			<VButton
				theme="primary"
				:disabled="!pendingFiles.length || isLoading"
				@click="uploadFiles()"
			>
				Добавить
			</VButton>

			<VButton
				:disabled="isLoading"
				@click="closeDialog()"
			>
				Отмена
			</VButton>
		</template>

		<VFilesDropZone
			v-if="
				FilesService.isDragging
				&& selectedEntity
				&& !isFileUploadConfirmationDialogOpen
			"
			:entity="selectedEntity"
			class="fullscreen"
			disable-upload
			@drop="handleLocalDrop"
		/>
	</VDialog>
</template>

<style lang="scss" src="./FilesSelectUploadDialog.scss"/>

<script lang="ts">
import Utils        from '@/core/utils/Utils';
import FilesService from '@/core/services/Files/FilesService';

export default defineComponent({
	name: 'FilesSelectUploadDialog',

	props: {
		/** @type EntityPointer[] */
		entities: {
			type:     Array,
			required: true,
		},

		/** файлы перетащенные вне дроп зоны */

		droppedFiles: {
			type:    Array,
			default: () => [],
		},
	},

	emits: ['close'],

	data() {
		return {
			Utils,
			FilesService,
			pendingFiles: [],
			selectedEntity: null,
			isLoading: false,

			isFileUploadConfirmationDialogOpen: false,
		};
	},

	mounted() {
		this.selectedEntity = this.entities?.[0] ?? null;

		/** подтверждение нужно не сразу при загрузке файлов в таблицу, а при сабмите */
		FilesService.config.showUploadConfirmation = false;
		/** блокируем глобальный d&d, у нас тут есть локальный */
		FilesService.lockDragDrop();

		/** сразу затащим файлы в список */

		if (this.droppedFiles.length) {
			this.pendingFiles.push(...this.droppedFiles);
		}
	},

	beforeUnmount() {
		/** закрыв мы вернемся в FilesList основной, возвращаем d&d */
		FilesService.unlockDragDrop();
	},

	methods: {
		/**
		 * Закрыть диалог, восстановить глобальные флаги Files-сервиса
		 * и эмитнуть событие, чтобы FilesList сбросил drag counter.
		 */
		closeDialog(): void {
			this.isLoading                             = false;
			FilesService.config.showUploadConfirmation = true;

			this.$emit('close');
			this.$core.dialogs.FilesSelectUploadDialog.hide();
		},

		/** два локальных метода дроп и селект для того чтобы была загрузка файлов в таблицу для предпросмотра,
		 * а не сразу на сервер, оригинальные методы не тронуты, все через флаг disableUpload у кнопки и зоны */

		handleLocalDrop(event) {
			const files = Array.from(event.dataTransfer?.files || []);

			if (files.length) {
				this.pendingFiles.push(...files);
			}
		},

		/**
		 * Локальный select файлов в таблицу предпросмотра — без отправки на сервер.
		 */
		handleLocalSelect(files: File[]): void {
			this.pendingFiles.push(...files);
		},

		/**
		 * Убрать файл из списка ожидающих. Удаление по идентичности объекта,
		 * так как V2-грид не передаёт index в slot.
		 */
		deletePendingFile(file: File): void {
			const index = this.pendingFiles.indexOf(file);
			if (index >= 0) {
				this.pendingFiles.splice(index, 1);
			}
		},

		/**
		 * Запросить подтверждение и отправить файлы на сервер. По отказу —
		 * вернуть форму в исходное состояние, по успеху — закрыть диалог.
		 */
		async uploadFiles(): Promise<void> {
			this.isLoading                          = true;
			this.isFileUploadConfirmationDialogOpen = true;

			if (!this.pendingFiles.length) return;

			const confirmed = await new Promise<boolean>(resolve => {
				this.$core.dialogs.VFilesUploadConfirmationDialog.show(
					{
						uploadFiles:  this.pendingFiles,
						targetEntity: this.selectedEntity,
					},
					{
						approve: () => {
							this.isFileUploadConfirmationDialogOpen = false;
							resolve(true);
						},
						cancel: () => {
							this.isFileUploadConfirmationDialogOpen = false;
							resolve(false);
						},
					},
				);
			});

			if (!confirmed) {
				this.isLoading = false;
				return;
			}

			await FilesService.uploadMultipleFiles(
				this.pendingFiles,
				this.selectedEntity,
			);

			this.closeDialog();
		},
	},
});
</script>
